import axios from 'axios';
import { config } from '../config/environment';

export interface ServiceHealth {
    service: string;
    status: 'initializing' | 'starting' | 'ready' | 'error' | 'unavailable';
    timestamp: string;
    responseTimeMs: number;
    message: string;
    details?: any;
    database?: {
        status: 'connected' | 'disconnected' | 'unknown';
        responseTime?: number;
    };
}

export interface SystemHealth {
    overall: 'healthy' | 'degraded' | 'unhealthy';
    services: ServiceHealth[];
    timestamp: string;
}

/**
 * WarmupService - Gateway-Centric Service Health Management
 * 
 * This service uses ONLY the API Gateway as the single entry point for all backend services.
 * No direct service calls are made - everything routes through the Gateway at port 5003.
 * 
 * Gateway Routes:
 * - /api/systemwarmup - Centralized warmup for all services
 * - /{service}/warmup - Individual service warmup through Gateway
 * - /{service}/health - Individual service health through Gateway
 */
class WarmupService {
    private readonly gatewayUrl = config.api.gateway;
    
    // Service names for routing through Gateway
    private readonly serviceNames = ['Auth', 'Staff', 'Client', 'Roster', 'Billing'];

    private async warmupSystemViaGateway(): Promise<SystemHealth> {
        try {
            //console.log('🚀 Warming up system via Gateway...');
            
            // Try the main system warmup endpoint first
            try {
                const response = await axios.get(`${this.gatewayUrl}/api/systemwarmup`, {
                    timeout: 10000,
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                });
                
                if (response.status === 200) {
                    //console.log('✅ Gateway system warmup successful');
                    return {
                        overall: 'healthy',
                        services: this.serviceNames.map(name => ({
                            service: name,
                            status: 'ready' as const,
                            timestamp: new Date().toISOString(),
                            responseTimeMs: 0,
                            message: 'Service warmed up successfully via gateway'
                        })),
                        timestamp: new Date().toISOString()
                    };
                }
            } catch (systemWarmupError: any) {
                if (systemWarmupError?.response?.status === 404) {
                    //console.log('⚠️ System warmup endpoint not available (404). Gateway may not be properly deployed.');
                    //console.log('🔄 Attempting individual service warmup as fallback...');
                    
                    // Fallback: Try individual service warmup endpoints
                    const services = ['auth', 'staff', 'clients', 'roster', 'billing'];
                    let successCount = 0;
                    const serviceResults: ServiceHealth[] = [];
                    
                    for (const service of services) {
                        try {
                            const serviceResponse = await axios.get(`${this.gatewayUrl}/${service}/warmup`, {
                                timeout: 5000
                            });
                            if (serviceResponse.status === 200) {
                                console.log(`✅ ${service} service warmup successful`);
                                successCount++;
                                serviceResults.push({
                                    service: service.charAt(0).toUpperCase() + service.slice(1),
                                    status: 'ready',
                                    timestamp: new Date().toISOString(),
                                    responseTimeMs: 0,
                                    message: 'Service warmed up successfully'
                                });
                            }
                        } catch (serviceError: any) {
                            console.log(`❌ ${service} service warmup failed:`, serviceError?.response?.status || 'Network error');
                            serviceResults.push({
                                service: service.charAt(0).toUpperCase() + service.slice(1),
                                status: 'error',
                                timestamp: new Date().toISOString(),
                                responseTimeMs: 0,
                                message: `Service warmup failed: ${serviceError?.response?.status || 'Network error'}`
                            });
                        }
                    }
                    
                    if (successCount > 0) {
                        console.log(`✅ ${successCount}/${services.length} services warmed up successfully`);
                        return {
                            overall: successCount === services.length ? 'healthy' : 'degraded',
                            services: serviceResults,
                            timestamp: new Date().toISOString()
                        };
                    } else {
                        console.log('❌ All individual service warmups failed. Production gateway appears to be down.');
                        return {
                            overall: 'unhealthy',
                            services: serviceResults,
                            timestamp: new Date().toISOString()
                        };
                    }
                } else {
                    console.log('❌ Gateway system warmup failed with unexpected error:', systemWarmupError?.response?.status || systemWarmupError.message);
                    return {
                        overall: 'unhealthy',
                        services: this.serviceNames.map(name => ({
                            service: name,
                            status: 'error' as const,
                            timestamp: new Date().toISOString(),
                            responseTimeMs: 0,
                            message: `Gateway error: ${systemWarmupError?.response?.status || systemWarmupError.message}`
                        })),
                        timestamp: new Date().toISOString()
                    };
                }
            }
            
            return {
                overall: 'unhealthy',
                services: [],
                timestamp: new Date().toISOString()
            };
        } catch (error: any) {
            console.error('❌ Gateway warmup failed:', error.message);
            if (error?.response?.status === 404) {
                console.log('⚠️ System warmup endpoint not available (404). Gateway may not be properly deployed.');
            }
            return {
                overall: 'unhealthy',
                services: this.serviceNames.map(name => ({
                    service: name,
                    status: 'error' as const,
                    timestamp: new Date().toISOString(),
                    responseTimeMs: 0,
                    message: `Gateway connection failed: ${error.message}`
                })),
                timestamp: new Date().toISOString()
            };
        }
    }

    private async testServiceViaGateway(serviceName: string): Promise<ServiceHealth> {
        const startTime = Date.now();
        
        try {
            //console.log(`🔍 Testing ${serviceName} service via Gateway...`);
            
            // Use Gateway routing for individual service warmup
            const warmupPath = `/${serviceName.toLowerCase()}/warmup`;
            const healthPath = `/${serviceName.toLowerCase()}/health`;
            
            const endpoints = [warmupPath, healthPath];
            
            let response;
            let lastError;
            
            for (const endpoint of endpoints) {
                try {
                    const url = `${this.gatewayUrl}${endpoint}`;
                    console.log(`  🔗 Trying ${serviceName} via Gateway: ${url}`);
                    
                    response = await axios.get(url, { 
                        timeout: 10000,
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json'
                        }
                    });
                    
                    console.log(`  ✅ ${serviceName} responded via Gateway`);
                    break;
                    
                } catch (endpointError: any) {
                    lastError = endpointError;
                    console.log(`  ❌ ${serviceName} endpoint ${endpoint} failed via Gateway:`, endpointError.message);
                    continue;
                }
            }

            if (!response) {
                throw lastError || new Error('All Gateway endpoints failed');
            }

            const responseTime = Date.now() - startTime;

            // Extract database information if available
            let databaseStatus = undefined;
            if (response.data) {
                if (response.data.database || response.data.checks?.database) {
                    databaseStatus = {
                        status: response.data.database === 'connected' || response.data.checks?.database === 'healthy' ? 'connected' : 'disconnected',
                        responseTime: response.data.databaseResponseTime
                    };
                }
            }

            return {
                service: serviceName,
                status: 'ready',
                timestamp: new Date().toISOString(),
                responseTimeMs: responseTime,
                message: response.data?.message || `${serviceName} service is ready via Gateway`,
                details: response.data,
                database: databaseStatus
            };

        } catch (error: any) {
            const responseTime = Date.now() - startTime;
            
            let status: 'error' | 'unavailable' = 'error';
            let message = `${serviceName} service error via Gateway`;

            if (error.code === 'ECONNREFUSED' || error.code === 'ENOTFOUND') {
                status = 'unavailable';
                message = `Gateway is not running or ${serviceName} service not available`;
            } else if (error.response) {
                if (error.response.status === 404) {
                    message = `${serviceName} service routing not configured in Gateway`;
                } else {
                    message = `${serviceName} service returned ${error.response.status} via Gateway: ${error.response.statusText}`;
                }
            } else if (error.request) {
                status = 'unavailable';
                message = `${serviceName} service not responding via Gateway (timeout)`;
            } else {
                message = `${serviceName} service error via Gateway: ${error.message}`;
            }

            //console.log(`❌ ${serviceName} health check via Gateway failed:`, message);

            return {
                service: serviceName,
                status,
                timestamp: new Date().toISOString(),
                responseTimeMs: responseTime,
                message,
                details: {
                    error: error.message,
                    code: error.code,
                    status: error.response?.status
                }
            };
        }
    }

    async checkSystemHealth(): Promise<SystemHealth> {
        //console.log('🔍 Starting system health check via Gateway...');
        
        // Use Gateway centralized warmup - this is the only entry point
        return await this.warmupSystemViaGateway();
    }

    async warmupServices(): Promise<SystemHealth> {
        //console.log('🚀 Warming up all services via Gateway (single entry point)...');
        
        // Gateway is the only entry point - no fallbacks
        return await this.warmupSystemViaGateway();
    }

    getServiceUrl(serviceName: string): string {
        // All services are accessed through Gateway routing
        return `${this.gatewayUrl}/${serviceName.toLowerCase()}`;
    }

    getAllServiceUrls(): Record<string, string> {
        return this.serviceNames.reduce((acc, serviceName) => {
            acc[serviceName.toLowerCase()] = `${this.gatewayUrl}/${serviceName.toLowerCase()}`;
            return acc;
        }, {} as Record<string, string>);
    }

    getServices() {
        // Return Gateway-routed services
        return this.serviceNames.map(name => ({
            name,
            url: `${this.gatewayUrl}/${name.toLowerCase()}`,
            port: 5003 // All go through Gateway
        }));
    }
}

export const warmupService = new WarmupService();
